import React from 'react';
import { StaticPage, TopbarContainer } from '..';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { propTypes } from '../../util/types';
import { bool, number } from 'prop-types';
import { createSlug } from '../../util/urlHelpers';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  FAQ,
  IconSuccess,
  PremiumPriceCalculator,
  NamedLink,
} from '../../components';

import css from './PremiumListingPage.module.css';
import trailerShieldLogo from '../../assets/trailer-shield-logo.png';
import nrmaIcon from '../RoadsidePage/assets/nrma-logo.png';
import conversationImage from '../../components/SectionForHirersFeatures/images/conversation.svg';
import promotionIcon from '../../components/SectionForBusinessBenefits/images/transfer.svg';
import discountIcon from '../../components/SectionForBusinessBenefits/images/price.svg';
import brandingIcon from '../../components/SectionForBusinessBenefits/images/work.svg';

const PremiumListingPage = props => {
  const {
    isAuthenticated,
    currentUserPublishedListingsCount,
    currentUserFirstPublishedListing = null,
    currentUser = null,
  } = props;

  const getButtonConfig = () => {
    if (!isAuthenticated || currentUserPublishedListingsCount === 0) {
      return {
        text: 'PremiumListingPage.createListing',
        linkName: 'NewListingPage',
        linkParams: {},
      };
    } else if (currentUserPublishedListingsCount === 1 && currentUserFirstPublishedListing) {
      return {
        text: 'PremiumListingPage.upgradeListingToPremium',
        linkName: 'EditListingPage',
        linkParams: {
          id: currentUserFirstPublishedListing.id.uuid,
          slug: createSlug(currentUserFirstPublishedListing.attributes.title),
          type: 'edit',
          tab: 'subscription',
        },
      };
    } else {
      return {
        text: 'PremiumListingPage.upgradeListingsToPremium',
        linkName: 'ManageListingsPage',
        linkParams: {},
      };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    <StaticPage
      title="Premium Listing"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'PremiumListingPage',
        description: 'Premium listing on localtrailerhire.com.au',
        name: 'Premium listing page',
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="PremiumListingPage" />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={css.staticPageWrapper}>
          <ul className={css.sections}>
            <li className={css.section}>
              <div className={`${css.sectionContent} ${css.columns}`}>
                <h1 className={css.sectionTitle}>Unlock More Bookings with a Premium Listing</h1>
                <div className={css.sectionContentPitch}>
                  <p>
                    At Local Trailer Hire, we know it takes more than just a listing to succeed with
                    trailer hire. That’s why we offer Premium Listings, giving you everything you
                    need to stand out, attract more customers, and grow your earnings.
                  </p>
                  <p>
                    Premium subscribers benefit from increased promotion to reach more hirers,
                    Trailer Shield damage cover and roadside assistance for peace of mind, and
                    exclusive features designed to boost your trailer's bookings.
                  </p>
                </div>

                <div>
                  <h2 className={css.sectionTitle}>Key Benefits:</h2>
                  <div className={css.glanceSection}>
                    <div className={css.glanceRow}>
                      <IconSuccess fillColor={'var(--marketplaceColor)'} />
                      <span>
                        <b>Trailer Shield damage cover</b> while your trailer is on hire
                      </span>
                    </div>
                    <div className={css.glanceRow}>
                      <IconSuccess fillColor={'var(--marketplaceColor)'} />
                      <span>
                        <b>Roadside Assistance by NRMA</b> to keep things moving
                      </span>
                    </div>
                    <div className={css.glanceRow}>
                      <IconSuccess fillColor={'var(--marketplaceColor)'} />
                      <span>
                        <b>Extra promotion</b> to boost your trailer's visibility
                      </span>
                    </div>
                    <div className={css.glanceRow}>
                      <IconSuccess fillColor={'var(--marketplaceColor)'} />
                      <span>
                        <b>Exclusive discount codes</b> you can share with customers
                      </span>
                    </div>
                    <div className={css.glanceRow}>
                      <IconSuccess fillColor={'var(--marketplaceColor)'} />
                      <span>
                        <b>Instant Book Option</b> for faster, easier hires
                      </span>
                    </div>
                    <div className={css.glanceRow}>
                      <IconSuccess fillColor={'var(--marketplaceColor)'} />
                      <span>
                        <b>Premium branding package</b> to help your trailer stand out
                      </span>
                    </div>
                    <div className={css.glanceRow}>
                      <IconSuccess fillColor={'var(--marketplaceColor)'} />
                      <span>
                        <b>Premium support</b> from our dedicated team
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h1 className={css.sectionTitle}>All the perks</h1>
                <ul className={css.benefitsSection}>
                  <li className={css.benefitCard}>
                    <NamedLink name="TrailerShieldPage">
                      <div className={css.benefitIcon}>
                        <img src={trailerShieldLogo} alt="Trailer Shield" />
                      </div>
                      <h3 className={css.benefitTitle}>Trailer Shield Damage Cover</h3>
                      <p className={css.benefitDescription}>
                        Get peace of mind with Trailer Shield, which provides damage cover while
                        your trailer is on hire (excludes personal use).
                      </p>
                      <p className={css.learnMore}>Learn more</p>
                    </NamedLink>
                  </li>
                  <li className={css.benefitCard}>
                    <NamedLink name="RoadsidePage">
                      <div className={css.benefitIcon}>
                        <img src={nrmaIcon} alt="NRMA" />
                      </div>
                      <h3 className={css.benefitTitle}>Roadside Assistance by NRMA</h3>
                      <p className={css.benefitDescription}>
                        We've partnered with NRMA to provide reliable roadside assistance during
                        hires. From flat tyres to mechanical issues, help is available when it
                        matters most.
                      </p>
                      <p className={css.learnMore}>Learn more</p>
                    </NamedLink>
                  </li>
                  <li className={css.benefitCard}>
                    <div className={css.benefitIcon}>
                      <img src={promotionIcon} alt="Promotion" />
                    </div>
                    <h3 className={css.benefitTitle}>Extra Promotion</h3>
                    <p className={css.benefitDescription}>
                      Stand out from the crowd with boosted visibility on our platform.
                    </p>
                  </li>
                  <li className={css.benefitCard}>
                    <div className={css.benefitIcon}>
                      <img src={discountIcon} alt="Discount" />
                    </div>
                    <h3 className={css.benefitTitle}>Exclusive Discount Codes</h3>
                    <p className={css.benefitDescription}>
                      Create and manage your own codes to reward repeat hirers or promote your
                      trailer. You can even set single-use codes.
                    </p>
                  </li>
                  <li className={css.benefitCard}>
                    <NamedLink name="InstantBookingPage">
                      <div className={css.benefitIcon}>
                        <span className={css.emojiIcon}>⚡️</span>
                      </div>
                      <h3 className={css.benefitTitle}>Instant Book Option</h3>
                      <p className={css.benefitDescription}>
                        If enabled, allows customers to book hires and receive instant confirmation,
                        without requiring you to confirm the booking.
                      </p>
                      <p className={css.learnMore}>Learn more</p>
                    </NamedLink>
                  </li>
                  <li className={css.benefitCard}>
                    <div className={css.benefitIcon}>
                      <img src={brandingIcon} alt="Branding" />
                    </div>
                    <span className={css.planBadge} aria-label="Included in Premium Monthly only">
                      Monthly only
                    </span>
                    <h3 className={css.benefitTitle}>Premium Branding Package</h3>
                    <p className={css.benefitDescription}>
                      Includes marketing tips, QR code stickers, and other materials to help your
                      trailer look professional and attract more hires.
                    </p>
                  </li>
                  <li className={`${css.benefitCard} ${css.monthlyOnly}`}>
                    <div className={css.benefitIcon}>
                      <img src={conversationImage} alt="Premium Support" />
                    </div>
                    <span className={css.planBadge} aria-label="Included in Premium Monthly only">
                      Monthly only
                    </span>
                    <h3 className={css.benefitTitle}>Premium Support</h3>
                    <p className={css.benefitDescription}>
                      Our dedicated team offers responsive and personalised support whenever you
                      need it.
                    </p>
                  </li>
                </ul>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h1 className={css.sectionTitle}>How much does it cost?</h1>
                <div className={css.pricingOptionsContainer}>
                  <div className={css.monthlyInfoBox}>
                    <h3>Premium Monthly</h3>
                    <p>
                      The price of a Premium Listing is based on your trailer's value. Enter your
                      trailer value below to see your monthly price.
                    </p>
                    <FinalForm
                      onSubmit={() => {}}
                      initialValues={{ marketValue: '' }}
                      render={({ form, values }) => (
                        <form>
                          <PremiumPriceCalculator
                            value={values.marketValue}
                            onChange={e => form.change('marketValue', e.target.value)}
                            label="Enter your trailer's market value"
                            showPriceMessage={true}
                          />
                        </form>
                      )}
                    />
                  </div>
                  <div className={css.paygInfoBox}>
                    <h3>Prefer Pay As You Go?</h3>
                    <p>
                      With <strong>Premium Pay As You Go</strong>, there's no upfront fee. A 10%
                      service fee is deducted from each hire payout. For example, if your daily
                      price is <strong>$50</strong>, you'll receive <strong>$45</strong> per hire.
                    </p>
                    <p className={css.note}>No hires, no charge.</p>
                  </div>
                </div>

                <div className={css.comparisonTableWrapper}>
                  <h2 className={css.tableTitle}>Compare Plans</h2>
                  <div className={css.tableContainer}>
                    <table className={css.comparisonTable}>
                      <thead>
                        <tr>
                          <th className={css.featureColumn}>Feature</th>
                          <th>Standard</th>
                          <th>Premium Pay As You Go</th>
                          <th>Premium Monthly</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className={css.featureCell}>Free listing</td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Secure payments</td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Control pricing and availability</td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Notifications</td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Owner dashboard</td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Help Centre</td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Trailer Shield damage cover</td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Roadside assistance</td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Extra promotion</td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Discount codes</td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Instant Book Option</td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Premium branding</td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                        <tr>
                          <td className={css.featureCell}>Priority support</td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                          <td className={css.crossCell}>
                            <span className={css.crossmark}>–</span>
                          </td>
                          <td className={css.checkCell}>
                            <span className={css.checkmark}>✓</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h1 className={css.sectionTitle}>FAQs</h1>
                <div className={css.faqSection}>
                  <FAQ category={'premium'} />
                  <FAQ category={'premium-payg'} />
                </div>
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <h1 className={css.sectionTitle}>Ready to get started?</h1>
                <div className={css.readySection}>
                  <div className={css.readySectionText}>
                    {isAuthenticated && currentUserPublishedListingsCount > 0 ? (
                      <p>
                        {currentUserPublishedListingsCount === 1
                          ? 'Upgrade your listing to Premium to unlock all the benefits.'
                          : 'Head to "My Listings" and click on "Upgrade to Premium" to upgrade your listings to Premium.'}
                      </p>
                    ) : (
                      <p>Create a listing and select the 'Premium' listing type.</p>
                    )}
                  </div>
                  <div className={css.readySectionButton}>
                    <NamedLink
                      className={css.premiumListingButton}
                      name={buttonConfig.linkName}
                      params={buttonConfig.linkParams}
                    >
                      <FormattedMessage id={buttonConfig.text} />
                    </NamedLink>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

PremiumListingPage.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserPublishedListingsCount: number.isRequired,
  currentUser: propTypes.currentUser,
  currentUserFirstPublishedListing: propTypes.ownListing,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.Auth;
  const { currentUserPublishedListingsCount, currentUserFirstPublishedListing } = state.user;
  return {
    isAuthenticated,
    currentUserPublishedListingsCount,
    currentUserFirstPublishedListing,
  };
};

export default compose(connect(mapStateToProps))(PremiumListingPage);
